import { useState } from 'react'
import { Grid2, Skeleton, Stack, Typography } from '@mui/material'
import {
  useGetLiveData,
  useGetVendorSettings,
  useUpdateVendorSettings,
} from '@/services/hooks/relay/dynamic'
import { OrgCredential } from '@/types/credentials'
import { LiveListEntity } from '@/types/live-lists'
import { BasePaymentMethod, BaseVendor } from '@/types/relay'
import { VendorSettings } from '@/types/vendor-settings'
import {
  GL_ACCOUNT_INFO_TYPES_IDS,
  PAYMENT_METHOD_INFO_TYPES_IDS,
} from '@/utils/info-types'
import { useCredentialTypeContext } from './CredentialTypeProvider'
import LiveListPicker from './LiveListPicker'
import useBootstrappedLiveListSelection from './useBootstrappedLiveListSelection'
import VendorAliasesTable from './VendorAliasesTable'
import VendorRollUpLineItemsSwitch from './VendorRollUpLineItemsSwitch'

type VendorSettingsFieldsProps = {
  credential: OrgCredential
  settingsOrgId: string
  vendor?: LiveListEntity<BaseVendor>
}

export default function VendorSettingsFields({
  credential,
  settingsOrgId,
  vendor,
}: VendorSettingsFieldsProps) {
  const [paymentMethodSearchValue, setPaymentMethodSearchValue] = useState('')
  const [accountSearchValue, setAccountSearchValue] = useState('')
  const credentialType = useCredentialTypeContext()
  const allowedConfiguration = credentialType.allowed_configuration

  const { vendorSettings, queryKey, isPending } = useGetVendorSettings({
    orgId: settingsOrgId,
    vendorId: vendor?.entity_id || '',
    orgCredentialTypeId: credential.type_id,
  })

  // Reset bootstrap when switching org/credential/vendor context.
  const listResetKey = `${settingsOrgId}:${credential.type_id}:${
    vendor?.entity_id || ''
  }`

  const paymentMethodSelection =
    useBootstrappedLiveListSelection<BasePaymentMethod>({
      infoTypeId: PAYMENT_METHOD_INFO_TYPES_IDS[0],
      orgId: settingsOrgId,
      orgCredentialTypeId: credential.type_id,
      enabled:
        !!vendor &&
        allowedConfiguration.includes('vendor_payment') &&
        !!vendorSettings,
      currentId: vendorSettings?.payment_method_id,
      searchValue: paymentMethodSearchValue,
      limit: '100',
      resetKey: `${listResetKey}:payment`,
    })

  const accountSelection = useBootstrappedLiveListSelection({
    infoTypeId: GL_ACCOUNT_INFO_TYPES_IDS[0],
    orgId: settingsOrgId,
    orgCredentialTypeId: credential.type_id,
    enabled:
      !!vendor &&
      allowedConfiguration.includes('vendor_gl') &&
      !!vendorSettings,
    currentId: vendorSettings?.default_gl_id,
    searchValue: accountSearchValue,
    limit: '10',
    resetKey: `${listResetKey}:account`,
  })

  const { allData: accounts, isPending: isPendingAccounts } = useGetLiveData({
    infoTypeId: GL_ACCOUNT_INFO_TYPES_IDS[0], // Since it's used as a hint, it's okay to pass any of them
    orgId: settingsOrgId,
    orgCredentialTypeId: credential.type_id,
    filters: {
      id: vendorSettings?.default_gl_id || '',
    },
    enabled:
      !!credentialType.supports.sync_default_gls &&
      !!vendorSettings?.default_gl_id,
  })

  const { updateVendorSettings } = useUpdateVendorSettings({
    orgId: settingsOrgId,
    vendorId: vendor?.entity_id as string,
    orgCredentialTypeId: credential.type_id,
    detailQueryKey: queryKey,
  })

  function handleChange(updatedValues: Partial<VendorSettings>) {
    if (vendorSettings) {
      updateVendorSettings({
        ...vendorSettings,
        ...updatedValues,
        id: vendorSettings.native_vendor_id, // for TS to be happy
      })
    }
  }

  return (
    <>
      <Grid2 container spacing={3}>
        {allowedConfiguration.includes('vendor_payment') && (
          <Grid2 size={{ xs: 12, md: 6 }}>
            <LiveListPicker
              value={paymentMethodSelection.selectedOption}
              options={paymentMethodSelection.options}
              onChange={(entity) => {
                handleChange({
                  payment_method_id: entity?.entity_id || null,
                })
              }}
              searchValue={paymentMethodSearchValue}
              setSearchValue={setPaymentMethodSearchValue}
              hasNextPage={paymentMethodSelection.hasNextPage}
              fetchNextPage={paymentMethodSelection.fetchNextPage}
              label="Payment Method"
              isLoading={paymentMethodSelection.isLoading}
              isFetching={paymentMethodSelection.isFetching}
              disabled={!vendor}
              disableClearable={false}
            />
          </Grid2>
        )}
        {allowedConfiguration.includes('vendor_gl') && (
          <Grid2 size={{ xs: 12, md: 6 }}>
            <LiveListPicker
              value={accountSelection.selectedOption}
              options={accountSelection.options}
              onChange={(entity) => {
                handleChange({
                  default_gl_id: entity?.entity_id || null,
                })
              }}
              searchValue={accountSearchValue}
              setSearchValue={setAccountSearchValue}
              hasNextPage={accountSelection.hasNextPage}
              fetchNextPage={accountSelection.fetchNextPage}
              label="Default GL Account"
              isLoading={accountSelection.isLoading}
              isFetching={accountSelection.isFetching}
              disabled={!vendor}
              disableClearable={false}
            />
          </Grid2>
        )}
        {credentialType.supports.sync_default_gls && (
          <Grid2 size={{ xs: 12, md: 6 }} sx={{ m: 'auto 0' }}>
            <Stack sx={{ minWidth: 0, px: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Default GL Account
              </Typography>
              <Typography component="div" noWrap>
                {isPending ||
                (vendorSettings?.default_gl_id && isPendingAccounts) ? (
                  <Skeleton width={200} />
                ) : (
                  accounts[0]?.pd_display_name || 'None'
                )}
              </Typography>
            </Stack>
          </Grid2>
        )}
        {allowedConfiguration.includes('vendor_roll_up') && (
          <Grid2 size={{ xs: 12 }}>
            <VendorRollUpLineItemsSwitch
              onChange={(checked) => {
                handleChange({
                  roll_up_line_items: checked,
                })
              }}
              checked={
                vendorSettings
                  ? vendorSettings.roll_up_line_items
                  : credential.roll_up_line_items_default
              }
              disabled={!vendor || isPending}
            />
          </Grid2>
        )}
      </Grid2>
      {allowedConfiguration.includes('vendor_alias') && (
        <VendorAliasesTable
          canEdit
          queryKey={queryKey}
          vendor={vendor}
          aliases={vendorSettings?.aliases || []}
          orgId={vendorSettings?.org_id || ''}
        />
      )}
    </>
  )
}
